<template>
    <div class="chat-sidebar">
        <div class="sidebar-content">
            <!-- Search Bar -->
            <div class="search-section">
                <div class="search-input-wrapper">
                    <i class="fas fa-search search-icon"></i>
                    <input 
                        type="text" 
                        class="search-input" 
                        placeholder="Search conversations..."
                        v-model="searchQuery"
                    />
                    <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>

            <!-- Tab Navigation -->
            <div class="tab-navigation">
                <button 
                    :class="['tab-btn', { active: tabActive == 'A' }]" 
                    @click="changeTab('A')"
                >
                    <div class="tab-content">
                        <div class="tab-icon">
                            <i class="fas fa-clock"></i>
                        </div>
                        <div class="tab-text">
                            <span class="tab-label">Pending</span>
                            <div class="tab-badge" v-if="countChatLists(pendingChats) > 0">
                                {{ countChatLists(pendingChats) }}
                            </div>
                        </div>
                    </div>
                </button>
                <button 
                    :class="['tab-btn', { active: tabActive == 'B' }]" 
                    @click="changeTab('B')"
                >
                    <div class="tab-content">
                        <div class="tab-icon">
                            <i class="fas fa-comments"></i>
                        </div>
                        <div class="tab-text">
                            <span class="tab-label">Active</span>
                            <div class="tab-badge" v-if="countChatLists(activeChats) > 0">
                                {{ countChatLists(activeChats) }}
                            </div>
                        </div>
                    </div>
                </button>
            </div>

            <!-- Category Filter -->
            <div class="category-section">
                <div class="section-header">
                    <div class="section-title">
                        <i class="fas fa-tags"></i>
                        <span>Categories</span>
                    </div>
                    <div class="section-count">
                        {{ category.length + 1 }}
                    </div>
                </div>
                <div class="category-chips">
                    <button 
                        :class="['category-chip', { active: selectedCategory === '' }]" 
                        @click="changeChatCategory('')"
                    >
                        <i class="fas fa-globe"></i>
                        <span>All</span>
                    </button>
                    <button 
                        :class="['category-chip', { active: selectedCategory === cat.name }]" 
                        v-for="(cat, idx) in category" 
                        :key="idx" 
                        @click="changeChatCategory(cat.name)"
                    >
                        <i class="fas fa-tag"></i>
                        <span>{{ cat.name }}</span>
                    </button>
                </div>
            </div>

            <!-- Chat List -->
            <div class="chat-list-section">
                <div v-if="tabActive == 'A'" class="chat-list-container">
                    <div v-if="!isChatListLoading" class="chat-list" ref="listContainer" @scroll="onListScroll">
                        <div :style="{ height: topSpacerPending + 'px' }"></div>
                        <div 
                            v-for="chatList in visiblePendingChats" 
                            :key="chatList.jid" 
                            class="chat-item" 
                            :class="{ 'selected': isSelected.includes(chatList.jid) }"
                        >
                            <div @click="openChat(chatList.jid, false)" class="chat-item-content">
                                <div class="avatar-container">
                                    <div class="avatar">
                                        <div class="avatar-initials" :style="getAvatarStyle(chatList.jid)">
                                            {{ getInitials(getName(chatList.jid, chatList.number)) }}
                                        </div>
                                    </div>
                                    <div class="status-indicator pending">
                                        <i class="fas fa-clock"></i>
                                    </div>
                                </div>
                                <div class="chat-info">
                                    <div class="chat-header">
                                        <h4 class="contact-name">{{ getName(chatList.jid, chatList.number) }}</h4>
                                        <div class="chat-meta">
                                            <span class="message-time">{{ formatDateRelative(chatList.last_update) }}</span>
                                            <div v-if="chatList.notification" class="unread-badge">
                                                {{ chatList.notification }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="chat-preview">
                                        <p class="last-message">{{ trimString(chatList.last_chat.text, 40) }}</p>
                                        <div class="message-status">
                                            <i class="fas fa-exclamation-circle"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="chat-actions">
                                <button @click="receiveChat(chatList.jid)" class="accept-btn">
                                    <i class="fas fa-check"></i>
                                    <span>Accept</span>
                                </button>
                            </div>
                        </div>
                        <div :style="{ height: bottomSpacerPending + 'px' }"></div>
                    </div>
                    <div v-else class="loading-container">
                        <div class="loading-spinner">
                            <div class="spinner"></div>
                        </div>
                        <p class="loading-text">Loading pending chats...</p>
                    </div>
                </div>

                <div v-else-if="tabActive == 'B'" class="chat-list-container">
                    <div v-if="!isChatListLoading" class="chat-list" ref="listContainer" @scroll="onListScroll">
                        <div :style="{ height: topSpacerActive + 'px' }"></div>
                        <div 
                            v-for="chatList in visibleActiveChats" 
                            :key="chatList.jid" 
                            class="chat-item" 
                            :class="{ 'selected': isSelected.includes(chatList.jid) }"
                        >
                            <div @click="openChat(chatList.jid, true)" class="chat-item-content">
                                <div class="avatar-container">
                                    <div class="avatar">
                                        <div class="avatar-initials" :style="getAvatarStyle(chatList.jid)">
                                            {{ getInitials(getName(chatList.jid, chatList.number)) }}
                                        </div>
                                    </div>
                                    <div class="status-indicator active">
                                        <i class="fas fa-check"></i>
                                    </div>
                                </div>
                                <div class="chat-info">
                                    <div class="chat-header">
                                        <h4 class="contact-name">{{ getName(chatList.jid, chatList.number) }}</h4>
                                        <div class="chat-meta">
                                            <span class="message-time">{{ formatDateRelative(chatList.last_update) }}</span>
                                            <div v-if="chatList.notification" class="unread-badge">
                                                {{ chatList.notification }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="chat-preview">
                                        <p class="last-message">{{ trimString(chatList.last_chat.text, 40) }}</p>
                                        <div class="message-status">
                                            <i class="fas fa-check-double"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div :style="{ height: bottomSpacerActive + 'px' }"></div>
                    </div>
                    <div v-else class="loading-container">
                        <div class="loading-spinner">
                            <div class="spinner"></div>
                        </div>
                        <p class="loading-text">Loading active chats...</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    name: 'ChatSidebar',
    props: {
        searchQuery: {
            type: String,
            default: ''
        },
        tabActive: {
            type: String,
            default: 'A'
        },
        pendingChats: {
            type: Array,
            default: () => []
        },
        activeChats: {
            type: Array,
            default: () => []
        },
        isChatListLoading: {
            type: Boolean,
            default: false
        },
        isSelected: {
            type: Array,
            default: () => []
        },
        category: {
            type: Array,
            default: () => []
        },
        selectedCategory: {
            type: String,
            default: ''
        }
    },
    computed: {
        itemHeight() {
            // Approximate height of each item (including margins)
            return 84;
        },
        filteredPendingChats() {
            let chats = this.pendingChats || [];
            if (this.searchQuery) {
                chats = chats.filter(chat => 
                    this.getName(chat.jid, chat.number).toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                    chat.last_chat.text.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            }
            return chats;
        },
        filteredActiveChats() {
            let chats = this.activeChats || [];
            if (this.searchQuery) {
                chats = chats.filter(chat => 
                    this.getName(chat.jid, chat.number).toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                    chat.last_chat.text.toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            }
            return chats;
        },
        // Virtualized windows
        visiblePendingChats() {
            return this.getVisibleWindow(this.filteredPendingChats, this.scrollTop, this.viewportHeight);
        },
        visibleActiveChats() {
            return this.getVisibleWindow(this.filteredActiveChats, this.scrollTop, this.viewportHeight);
        }
    },
    methods: {
        onListScroll(e) {
            const el = e.target;
            this.scrollTop = el.scrollTop;
            this.viewportHeight = el.clientHeight;
        },
        getVisibleWindow(list, scrollTop, viewportHeight) {
            const itemH = this.itemHeight;
            const startIndex = Math.max(Math.floor(scrollTop / itemH) - 5, 0);
            const visibleCount = Math.ceil((viewportHeight || 600) / itemH) + 10;
            const endIndex = Math.min(startIndex + visibleCount, list.length);
            this.topSpacerPending = 0;
            this.bottomSpacerPending = 0;
            this.topSpacerActive = 0;
            this.bottomSpacerActive = 0;
            const top = startIndex * itemH;
            const bottom = (list.length - endIndex) * itemH;
            if (this.tabActive === 'A') {
                this.topSpacerPending = top;
                this.bottomSpacerPending = bottom;
            } else {
                this.topSpacerActive = top;
                this.bottomSpacerActive = bottom;
            }
            return list.slice(startIndex, endIndex);
        },
        changeTab(tab) {
            this.$emit('change-tab', tab);
        },
        changeChatCategory(category) {
            this.$emit('change-category', category);
        },
        openChat(jid, sendable) {
            this.$emit('open-chat', { jid, sendable });
        },
        receiveChat(jid) {
            this.$emit('receive-chat', jid);
        },
        getInitials(name) {
            try {
                const cleaned = (name || '').toString().trim();
                if (!cleaned) return '?';
                // If it's a number (e.g., phone), take last 2-3 digits
                if (/^\d[\d\s+()-]*$/.test(cleaned)) {
                    const digits = cleaned.replace(/\D/g, '');
                    return digits.slice(-2).toUpperCase();
                }
                const parts = cleaned.split(/\s+/).filter(Boolean);
                if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
                return (parts[0][0] + parts[1][0]).toUpperCase();
            } catch (e) {
                return '?';
            }
        },
        getAvatarStyle(seed) {
            // Deterministic pastel background based on jid hash
            const colors = ['#F8BBD0', '#BBDEFB', '#C8E6C9', '#FFE0B2', '#D1C4E9', '#B2DFDB', '#FFCDD2', '#DCEDC8'];
            let hash = 0;
            const str = (seed || '').toString();
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash |= 0;
            }
            const index = Math.abs(hash) % colors.length;
            return {
                background: colors[index],
                color: '#1a1a1a'
            };
        },
        getName(jid, number) {
            // This should be passed from parent or computed from store
            return this.$parent.getName ? this.$parent.getName(jid, number) : number;
        },
        formatDateRelative(unix) {
            return moment(unix).fromNow();
        },
        trimString(string, length) {
            if (string == null || string == "") {
                return "";
            }
            return string.length > length ? string.substring(0, length) + '...' : string;
        },
        countChatLists(obj) {
            try {
                return obj.length;
            } catch (err) {
                return 0;
            }
        }
    },
    data() {
        return {
            scrollTop: 0,
            viewportHeight: 600,
            topSpacerPending: 0,
            bottomSpacerPending: 0,
            topSpacerActive: 0,
            bottomSpacerActive: 0
        };
    },
    mounted() {
        // Initialize viewport height if element exists
        this.$nextTick(() => {
            const el = this.$refs.listContainer;
            if (el) {
                this.viewportHeight = el.clientHeight;
            }
        });
    }
};
</script>

<style scoped>
/* Sidebar */
.chat-sidebar {
    width: 400px;
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-right: 1px solid #e3f2fd;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.06);
    position: relative;
}

.chat-sidebar::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1976d2, #42a5f5, #1976d2);
}

.sidebar-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Header */
.sidebar-header {
    background: white;
    border-bottom: 1px solid #e3f2fd;
    padding: 16px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.sidebar-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.25px;
}

.header-actions {
    display: flex;
    gap: 8px;
}

.refresh-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f5f5f5;
    border: none;
    color: #757575;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.8rem;
}

.refresh-btn:hover {
    background: #e3f2fd;
    color: #1976d2;
    transform: scale(1.05);
}

/* Search Section */
.search-section {
    padding: 16px 20px;
    background: white;
    border-bottom: 1px solid #f0f0f0;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #9e9e9e;
    font-size: 0.9rem;
    z-index: 2;
}

.search-input {
    width: 100%;
    padding: 10px 14px 10px 40px;
    border: 1px solid #f0f0f0;
    border-radius: 20px;
    font-size: 0.9rem;
    background: #fafafa;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 400;
    letter-spacing: 0.25px;
}

.search-input:focus {
    outline: none;
    border-color: #1976d2;
    background: white;
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.1);
    transform: translateY(-1px);
}

.search-input::placeholder {
    color: #9e9e9e;
    font-size: 0.9rem;
    font-weight: 400;
}

.clear-search {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #e0e0e0;
    border: none;
    color: #757575;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-search:hover {
    background: #f44336;
    color: white;
    transform: translateY(-50%) scale(1.1);
}

/* Tab Navigation */
.tab-navigation {
    display: flex;
    background: white;
    margin: 0 20px 12px;
    border-radius: 8px;
    padding: 4px;
    gap: 2px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
}

.tab-btn {
    flex: 1;
    background: transparent;
    border: none;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
}

.tab-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    opacity: 0;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 8px;
}

.tab-btn.active::before {
    opacity: 1;
}

.tab-btn.active {
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.tab-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-weight: 500;
    color: #757575;
    font-size: 0.85rem;
    letter-spacing: 0.25px;
    position: relative;
    z-index: 1;
}

.tab-btn.active .tab-content {
    color: white;
}

.tab-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
}

.tab-text {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
}

.tab-label {
    font-weight: 600;
    font-size: 0.8rem;
}

.tab-badge {
    background: #f44336;
    color: white;
    border-radius: 10px;
    padding: 2px 8px;
    font-size: 0.7rem;
    font-weight: 600;
    min-width: 18px;
    text-align: center;
    line-height: 1.2;
    box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
}

.tab-btn.active .tab-badge {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Category Section */
.category-section {
    padding: 16px 20px;
    background: white;
    border-bottom: 1px solid #f0f0f0;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #1a1a1a;
    font-size: 0.9rem;
    letter-spacing: 0.25px;
}

.section-title i {
    color: #1976d2;
    font-size: 1rem;
}

.section-count {
    background: #e3f2fd;
    color: #1976d2;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    min-width: 20px;
    text-align: center;
}

.category-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.category-chip {
    background: #f8f9fa;
    border: 1px solid #f0f0f0;
    color: #666;
    padding: 8px 12px;
    border-radius: 16px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: 0.25px;
    display: flex;
    align-items: center;
    gap: 4px;
    position: relative;
    overflow: hidden;
}

.category-chip::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s;
}

.category-chip:hover::before {
    left: 100%;
}

.category-chip:hover {
    background: #e3f2fd;
    color: #1976d2;
    border-color: #1976d2;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

.category-chip.active {
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    color: white;
    border-color: #1976d2;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
    transform: translateY(-1px);
}

/* Chat List Section */
.chat-list-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: #fafafa;
}

.chat-list-container {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.chat-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
}

.chat-item {
    margin: 0 12px 6px;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    background: white;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
    overflow: hidden;
}

.chat-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1976d2, #42a5f5);
    opacity: 0;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-item:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: #e3f2fd;
}

.chat-item.selected {
    background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
    border-color: #1976d2;
    box-shadow: 0 8px 25px rgba(25, 118, 210, 0.2);
    transform: translateY(-1px);
}

.chat-item.selected::before {
    opacity: 1;
}

.chat-item-content {
    display: flex;
    align-items: center;
    padding: 16px;
    gap: 12px;
    cursor: pointer;
    position: relative;
}

.avatar-container {
    position: relative;
    flex-shrink: 0;
}

.avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #f0f0f0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.avatar-initials {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
    text-transform: uppercase;
}

.chat-item:hover .avatar {
    border-color: #e3f2fd;
    transform: scale(1.05);
}

.status-indicator {
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5rem;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.status-indicator.pending {
    background: linear-gradient(135deg, #ffc107, #ff9800);
    color: white;
}

.status-indicator.active {
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
}

.chat-info {
    flex: 1;
    min-width: 0;
}

.chat-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 6px;
}

.contact-name {
    font-size: 1rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: -0.25px;
    line-height: 1.3;
}

.chat-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.message-time {
    font-size: 0.8rem;
    color: #9e9e9e;
    font-weight: 500;
    letter-spacing: 0.25px;
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 10px;
}

.chat-preview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.last-message {
    font-size: 0.85rem;
    color: #666;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    font-weight: 400;
    letter-spacing: 0.25px;
    line-height: 1.4;
}

.message-status {
    display: flex;
    align-items: center;
    color: #9e9e9e;
    font-size: 0.8rem;
}

.unread-badge {
    background: linear-gradient(135deg, #f44336, #e91e63);
    color: white;
    border-radius: 12px;
    padding: 4px 8px;
    font-size: 0.7rem;
    font-weight: 600;
    min-width: 20px;
    text-align: center;
    line-height: 1.2;
    box-shadow: 0 2px 4px rgba(244, 67, 54, 0.3);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
}

.chat-actions {
    padding: 10px 16px;
    border-top: 1px solid #f0f0f0;
    background: #fafafa;
}

.accept-btn {
    background: linear-gradient(135deg, #4caf50, #66bb6a);
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: 0.25px;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
    position: relative;
    overflow: hidden;
}

.accept-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
}

.accept-btn:hover::before {
    left: 100%;
}

.accept-btn:hover {
    background: linear-gradient(135deg, #388e3c, #4caf50);
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
    transform: translateY(-2px);
}

.accept-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

/* Loading States */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    height: 100%;
    background: #fafafa;
}

.loading-spinner {
    margin-bottom: 20px;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f0f0f0;
    border-top: 4px solid #1976d2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-text {
    color: #666;
    font-size: 0.95rem;
    margin: 0;
    font-weight: 500;
    letter-spacing: 0.25px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Responsive Design */
@media (max-width: 768px) {
    .chat-sidebar {
        width: 100%;
        max-height: 45vh;
        min-height: 250px;
    }

    .sidebar-header {
        padding: 12px 16px;
    }

    .sidebar-title {
        font-size: 1.1rem;
    }

    .search-section {
        padding: 12px 16px;
    }

    .tab-navigation {
        margin: 0 16px 10px;
    }

    .category-section {
        padding: 12px 16px;
    }

    .chat-item {
        margin: 0 10px 4px;
    }

    .chat-item-content {
        padding: 12px;
        gap: 10px;
    }

    .avatar {
        width: 40px;
        height: 40px;
    }

    .contact-name {
        font-size: 0.95rem;
    }

    .last-message {
        font-size: 0.8rem;
    }
}
</style>
